import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableCell,
  TextField,
  Checkbox,
  Snackbar,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SpeedIcon from '@mui/icons-material/Speed';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import HistoryIcon from '@mui/icons-material/History';
import EuroIcon from '@mui/icons-material/Euro';
import BuildIcon from '@mui/icons-material/Build';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { metersApi, meterReadingsApi, interventionNotesApi } from '../../services/api';
import { formatCustomerName } from '../../utils/formatCustomerName';
import { getApiBaseUrl } from '../../utils/runtimeUrls';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

// ============================================
// COMPONENTE: MeterPhotosSection
// ============================================
const METER_API_BASE_URL = getApiBaseUrl();

const MeterPhotosSection = ({ meterId, plantLatitude, plantLongitude }) => {
  const queryClient = useQueryClient();
  const [photoDialog, setPhotoDialog] = useState({ open: false, photos: [], index: 0 });

  const photosQuery = useQuery({
    queryKey: ['meter-pwa-photos', meterId],
    queryFn: () => meterReadingsApi.getMeterPhotos(meterId).then(res => res.data?.data),
    enabled: !!meterId
  });

  const deleteMutation = useMutation({
    mutationFn: (photoId) => meterReadingsApi.deletePhoto(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meter-pwa-photos', meterId] });
    }
  });

  const photos = photosQuery.data?.photos || [];

  if (photosQuery.isLoading) return <LinearProgress />;
  if (photos.length === 0) return null;

  const formatPhotoDate = (dateValue) => {
    if (!dateValue) return '-';
    try {
      let d = dateValue;
      if (typeof d === 'string' && !d.endsWith('Z') && !d.includes('+')) d += 'Z';
      const date = new Date(d);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy HH:mm', { locale: it });
    } catch { return '-'; }
  };

  // Haversine: distanza in linea d'aria tra due coordinate GPS (ritorna metri)
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (v) => (v * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const formatDistance = (photoLat, photoLng) => {
    if (!photoLat || !photoLng || !plantLatitude || !plantLongitude) return null;
    const dist = haversineDistance(parseFloat(photoLat), parseFloat(photoLng), parseFloat(plantLatitude), parseFloat(plantLongitude));
    if (isNaN(dist)) return null;
    return dist < 1000 ? `${Math.round(dist)} m` : `${(dist / 1000).toFixed(1)} km`;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <PhotoLibraryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Foto PWA ({photos.length})
        </Typography>
        <Grid container spacing={{ xs: 1, sm: 1.5 }}>
          {photos.map((photo, idx) => (
            <Grid item key={photo.id}>
              <Card
                sx={{ width: { xs: 150, sm: 200 }, cursor: 'pointer', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}
                onClick={() => setPhotoDialog({ open: true, photos, index: idx })}
              >
                <CardMedia
                  component="img"
                  height="130"
                  image={`${METER_API_BASE_URL}/meter-readings/photo/${photo.filename}`}
                  alt={photo.original_name || 'Foto'}
                  sx={{ objectFit: 'cover' }}
                  onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="130"><rect fill="%23eee" width="200" height="130"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999" font-size="12">Foto</text></svg>'; }}
                />
                <Box sx={{ px: 1, py: 0.5, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatPhotoDate(photo.created_at)}
                      </Typography>
                    </Box>
                    <Tooltip title="Elimina foto">
                      <IconButton size="small" color="error" onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Eliminare questa foto?')) deleteMutation.mutate(photo.id);
                      }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {(photo.gps_latitude && photo.gps_longitude) && (() => {
                    const dist = formatDistance(photo.gps_latitude, photo.gps_longitude);
                    return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <LocationOnIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                        {parseFloat(photo.gps_latitude).toFixed(5)}, {parseFloat(photo.gps_longitude).toFixed(5)}
                        {' '}
                        <a
                          href={`https://maps.google.com/?q=${photo.gps_latitude},${photo.gps_longitude}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ color: '#1976d2', fontSize: 10 }}
                          onClick={(e) => e.stopPropagation()}
                        >Maps</a>
                      </Typography>
                      {dist && (
                        <Chip label={`↔ ${dist}`} size="small" sx={{ height: 18, fontSize: 10, ml: 0.5 }} color={parseFloat(dist) > 5 ? 'warning' : 'default'} />
                      )}
                    </Box>
                    );
                  })()}
                  {photo.operatore && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                      Op: {photo.operatore}
                    </Typography>
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Photo Lightbox Dialog */}
        <Dialog
          open={photoDialog.open}
          onClose={() => setPhotoDialog({ open: false, photos: [], index: 0 })}
          maxWidth="md" fullWidth
        >
          <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1">
              Foto {photoDialog.index + 1} di {photoDialog.photos.length}
            </Typography>
            <IconButton onClick={() => setPhotoDialog({ open: false, photos: [], index: 0 })}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
            {photoDialog.photos[photoDialog.index] && (
              <img
                src={`${METER_API_BASE_URL}/meter-readings/photo/${photoDialog.photos[photoDialog.index]?.filename}`}
                alt={photoDialog.photos[photoDialog.index]?.original_name || 'Foto'}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            )}
          </DialogContent>
          {photoDialog.photos.length > 1 && (
            <DialogActions sx={{ justifyContent: 'center' }}>
              <Button disabled={photoDialog.index === 0} onClick={() => setPhotoDialog(p => ({ ...p, index: p.index - 1 }))}>← Precedente</Button>
              <Button disabled={photoDialog.index >= photoDialog.photos.length - 1} onClick={() => setPhotoDialog(p => ({ ...p, index: p.index + 1 }))}>Successiva →</Button>
            </DialogActions>
          )}
        </Dialog>
      </CardContent>
    </Card>
  );
};

const MeterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Stato locale
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Query contatore
  const { data: meterData, isLoading, error } = useQuery({
    queryKey: ['meter', id],
    queryFn: async () => {
      const response = await metersApi.getById(id);
      return response.data;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0,
    cacheTime: 0
  });

  // Query storico letture
  const { data: readingsData } = useQuery({
    queryKey: ['meter-readings-history', id],
    queryFn: async () => {
      const response = await meterReadingsApi.getHistory(id, { limit: 10 });
      return response.data;
    }
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Errore nel caricamento del contatore: {error.message}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/crm/contatori')} sx={{ mt: 2 }}>
          Torna alla lista
        </Button>
      </Box>
    );
  }

  const meter = meterData?.data || meterData;
  const readings = readingsData?.data?.readings || [];

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const formatNumber = (num, decimals = 0) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('it-IT', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, minWidth: 0 }}>
          <IconButton onClick={() => navigate('/crm/contatori')} size={isMobile ? 'small' : 'medium'}>
            <ArrowBackIcon />
          </IconButton>
          {!isMobile && <SpeedIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" noWrap>
              Contatore {meter.nu_conta || meter.c_cod_cont || meter.id}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Progr: {meter.sotto_zona || meter.c_sotto_zo || meter.c_nu_conta || meter.id}
            </Typography>
          </Box>
          <Chip 
            label={meter.stato || 'attivo'} 
            color={meter.stato === 'attivo' ? 'success' : meter.stato === 'sospeso' ? 'warning' : 'error'} 
            size="small" 
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/crm/contatori/${id}/modifica`)}
          size={isMobile ? 'small' : 'medium'}
        >
          Modifica
        </Button>
      </Box>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Dati Contatore */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SpeedIcon color="primary" />
                <Typography variant="h6">Dati Contatore</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Matricola</TableCell>
                    <TableCell>{meter.nu_conta || meter.c_cod_cont || meter.meter_number || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Progressivo</TableCell>
                    <TableCell>{meter.sotto_zona || meter.c_sotto_zo || meter.c_nu_conta || meter.id}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Sottozona</TableCell>
                    <TableCell>{meter.sotto_zona || meter.c_sotto_zo || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Stato</TableCell>
                    <TableCell>
                      <Chip 
                        label={meter.stato || 'attivo'} 
                        color={meter.stato === 'attivo' ? 'success' : 'default'} 
                        size="small" 
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Intestatario e Ubicazione */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOnIcon color="primary" />
                <Typography variant="h6">Intestatario e Ubicazione</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Intestatario</TableCell>
                    <TableCell>{meter.intestatario || meter.c_nome || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Località</TableCell>
                    <TableCell>{meter.localita || meter.c_localita || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Città</TableCell>
                    <TableCell>{meter.citta || meter.c_citta || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Provincia</TableCell>
                    <TableCell>{meter.provincia || meter.c_provinci || '-'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Cliente Associato */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6">Cliente Associato</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {meter.customer_id ? (
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCustomerName(meter.customer, null) || meter.Customer?.c_ragsoc || meter.Customer?.c_nome || `Cliente #${meter.customer_id}`}
                  </Typography>
                  {(meter.customer?.cod_cli || meter.Customer?.c_codice) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Codice: {meter.customer?.cod_cli || meter.Customer?.c_codice}
                    </Typography>
                  )}
                  {(meter.customer?.citta || meter.Customer?.c_citta) && (
                    <Typography variant="body2" color="text.secondary">
                      {meter.customer?.citta || meter.Customer?.c_citta}
                    </Typography>
                  )}
                  <Button
                    component={RouterLink}
                    to={`/crm/clienti/${meter.customer_id}`}
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                  >
                    Vai al Cliente
                  </Button>
                </Box>
              ) : (
                <Typography color="text.secondary">Nessun cliente associato</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Impianto Associato */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocalGasStationIcon color="primary" />
                <Typography variant="h6">Impianto Associato</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {meter.plant_id ? (
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {meter.plant?.codice || meter.Plant?.p_codice || `Impianto #${meter.plant_id}`}
                  </Typography>
                  {(meter.plant?.nome || meter.Plant?.p_nome) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {meter.plant?.nome || meter.Plant?.p_nome}
                    </Typography>
                  )}
                  {(meter.plant?.indirizzo || meter.Plant?.p_indirizzo) && (
                    <Typography variant="body2" color="text.secondary">
                      {meter.plant?.indirizzo || meter.Plant?.p_indirizzo}
                      {(meter.plant?.citta || meter.Plant?.p_citta) && ` - ${meter.plant?.citta || meter.Plant?.p_citta}`}
                    </Typography>
                  )}
                  <Button
                    component={RouterLink}
                    to={`/crm/impianti/${meter.plant_id}`}
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                  >
                    Vai all'Impianto
                  </Button>
                </Box>
              ) : (
                <Typography color="text.secondary">Nessun impianto associato</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Letture e Consumi */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HistoryIcon color="primary" />
                <Typography variant="h6">Letture e Consumi</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Ultima Lettura</TableCell>
                    <TableCell>{meter.c_ult_lett || meter.ultima_lettura ? `${formatNumber(meter.c_ult_lett || meter.ultima_lettura)} mc` : '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data Lettura</TableCell>
                    <TableCell>{formatDate(meter.c_ult_data || meter.data_ultima_lettura)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Consumo Reale</TableCell>
                    <TableCell>{meter.c_cons_rea ? `${formatNumber(meter.c_cons_rea)} mc` : '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Consumo Totale</TableCell>
                    <TableCell>{meter.c_consumo ? `${formatNumber(meter.c_consumo)} mc` : '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Lettura Precedente</TableCell>
                    <TableCell>{meter.c_ant_lett || meter.lettura_precedente ? `${formatNumber(meter.c_ant_lett || meter.lettura_precedente)} mc` : '-'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Prezzi */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EuroIcon color="primary" />
                <Typography variant="h6">Prezzi</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Prezzo al Litro</TableCell>
                    <TableCell>{meter.c_prez_lit || meter.prezzo_litro ? `€ ${formatNumber(meter.c_prez_lit || meter.prezzo_litro, 4)}` : '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Aliquota IVA</TableCell>
                    <TableCell>{meter.c_iva || meter.aliquota_iva ? `${formatNumber(meter.c_iva || meter.aliquota_iva, 2)}%` : '-'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Storico Letture Recenti */}
        {readings.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <HistoryIcon color="primary" />
                  <Typography variant="h6">Ultime Letture</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableBody>
                    {readings.map((reading, index) => (
                      <TableRow key={reading.id || index}>
                        <TableCell>{formatDate(reading.data_lettura)}</TableCell>
                        <TableCell>{formatNumber(reading.lettura_mc)} mc</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          Consumo: {reading.consumo_mc ? `${formatNumber(reading.consumo_mc)} mc` : '-'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip 
                              label={reading.tipo_lettura === 'stimata' ? 'stimata' : 'reale'} 
                              size="small" 
                              variant="outlined"
                              color={reading.tipo_lettura === 'stimata' ? 'warning' : 'default'}
                            />
                            {reading.da_fatturare === false && (
                              <Chip 
                                label="non fatturata" 
                                size="small" 
                                variant="filled"
                                color="error"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Note Intervento */}
        <Grid item xs={12}>
          <InterventionNotesSection
            meterId={id}
            plantId={meter?.plant_id}
            showAddNote={showAddNote}
            setShowAddNote={setShowAddNote}
            newNoteText={newNoteText}
            setNewNoteText={setNewNoteText}
            showCompleted={showCompleted}
            setShowCompleted={setShowCompleted}
            snackbar={snackbar}
            setSnackbar={setSnackbar}
            queryClient={queryClient}
          />
        </Grid>

        {/* Foto PWA Contatore */}
        <Grid item xs={12}>
          <MeterPhotosSection meterId={id} plantLatitude={meter?.plant?.latitude || meter?.Plant?.latitude} plantLongitude={meter?.plant?.longitude || meter?.Plant?.longitude} />
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/**
 * Sottosezione Note Intervento per contatore
 */
const InterventionNotesSection = ({ meterId, plantId, showAddNote, setShowAddNote, newNoteText, setNewNoteText, showCompleted, setShowCompleted, snackbar, setSnackbar, queryClient }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [completingNote, setCompletingNote] = useState(null);
  const [completionText, setCompletionText] = useState('');
  // Query note intervento
  const { data: notesData, isLoading: notesLoading } = useQuery({
    queryKey: ['intervention-notes', 'meter', meterId],
    queryFn: async () => {
      const response = await interventionNotesApi.getByMeter(meterId);
      return response.data?.data || [];
    },
    enabled: !!meterId
  });

  const notes = notesData || [];
  const activeNotes = notes.filter(n => !n.completata);
  const completedNotes = notes.filter(n => n.completata);

  // Mutation: crea nota
  const createMutation = useMutation({
    mutationFn: (data) => interventionNotesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intervention-notes', 'meter', meterId] });
      setSnackbar({ open: true, message: 'Nota intervento aggiunta', severity: 'success' });
      setNewNoteText('');
      setShowAddNote(false);
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Errore nel salvataggio', severity: 'error' });
    }
  });

  // Mutation: completa nota
  const completeMutation = useMutation({
    mutationFn: ({ noteId, note_completamento }) => interventionNotesApi.complete(noteId, { note_completamento }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intervention-notes', 'meter', meterId] });
      setCompletingNote(null);
      setCompletionText('');
      setSnackbar({ open: true, message: 'Intervento completato', severity: 'success' });
    }
  });

  // Mutation: riapri nota
  const reopenMutation = useMutation({
    mutationFn: (noteId) => interventionNotesApi.reopen(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intervention-notes', 'meter', meterId] });
    }
  });

  // Mutation: elimina nota
  const deleteMutation = useMutation({
    mutationFn: (noteId) => interventionNotesApi.delete(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intervention-notes', 'meter', meterId] });
      setSnackbar({ open: true, message: 'Nota eliminata', severity: 'success' });
    }
  });

  const handleSaveNote = () => {
    if (!newNoteText.trim()) return;
    createMutation.mutate({
      meter_id: parseInt(meterId),
      plant_id: plantId ? parseInt(plantId) : null,
      tipo: 'contatore',
      nota: newNoteText.trim()
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT');
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon color="warning" />
            <Typography variant="h6">Note Intervento</Typography>
            {activeNotes.length > 0 && (
              <Chip label={activeNotes.length} size="small" color="warning" />
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={<AddIcon />}
            onClick={() => setShowAddNote(!showAddNote)}
          >
            Aggiungi nota
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Form aggiunta nota */}
        <Collapse in={showAddNote}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              multiline
              rows={2}
              fullWidth
              placeholder="Descrivi l'intervento da fare su questo contatore..."
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveNote();
                }
              }}
            />
            <Button
              variant="contained"
              color="warning"
              onClick={handleSaveNote}
              disabled={createMutation.isPending || !newNoteText.trim()}
              sx={{ minWidth: 90 }}
            >
              {createMutation.isPending ? <CircularProgress size={20} /> : 'Salva'}
            </Button>
          </Box>
        </Collapse>

        {/* Note attive */}
        {notesLoading ? (
          <CircularProgress size={24} />
        ) : activeNotes.length === 0 && completedNotes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nessuna nota di intervento per questo contatore.</Typography>
        ) : (
          <>
            {activeNotes.map(note => (
              <Box key={note.id} sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                mb: 0.5,
                bgcolor: '#fff3e0',
                borderLeft: '4px solid #ff9800',
                borderRadius: 1
              }}>
                <Checkbox
                  checked={false}
                  onChange={() => { setCompletingNote(note); setCompletionText(''); }}
                  color="warning"
                  size="small"
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#e65100' }}>
                    <WarningAmberIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    {note.nota}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    Creata il {formatDate(note.data_creazione)}{note.operatore ? ` da ${note.operatore}` : ''}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => deleteMutation.mutate(note.id)} sx={{ color: '#999' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            {/* Note completate */}
            {completedNotes.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => setShowCompleted(!showCompleted)}
                  sx={{ mb: 0.5 }}
                >
                  {showCompleted ? 'Nascondi' : 'Mostra'} completate ({completedNotes.length})
                </Button>
                <Collapse in={showCompleted}>
                  {completedNotes.map(note => (
                    <Box key={note.id} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      mb: 0.5,
                      bgcolor: '#f5f5f5',
                      borderLeft: '4px solid #4caf50',
                      borderRadius: 1,
                      opacity: 0.7
                    }}>
                      <Checkbox
                        checked={true}
                        onChange={() => reopenMutation.mutate(note.id)}
                        color="success"
                        size="small"
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: '#4caf50' }} />
                          {note.nota}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          Completata il {formatDate(note.data_completamento)}
                        </Typography>
                        {note.note_completamento && (
                          <Typography variant="body2" sx={{ mt: 0.5, color: '#2e7d32', fontStyle: 'italic', fontSize: '0.8rem' }}>
                            💬 {note.note_completamento}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={() => deleteMutation.mutate(note.id)} sx={{ color: '#999' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Collapse>
              </Box>
            )}
          </>
        )}
      </CardContent>

      {/* Dialog Completamento Nota */}
      <Dialog open={!!completingNote} onClose={() => setCompletingNote(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Completa Intervento</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1, p: 1.5, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold">{completingNote?.nota}</Typography>
            <Typography variant="caption" color="text.secondary">
              Creata il {completingNote?.data_creazione ? formatDate(completingNote.data_creazione) : ''}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Note operative (facoltative)"
            value={completionText}
            onChange={(e) => setCompletionText(e.target.value)}
            placeholder="Descrivi i dettagli dell'operazione effettuata..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletingNote(null)}>Annulla</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => completeMutation.mutate({ noteId: completingNote.id, note_completamento: completionText.trim() || null })}
            disabled={completeMutation.isPending}
          >
            Conferma Completamento
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default MeterDetail;
